import React, { useState } from "react";
import { Form, Formik, FormikConfig } from "formik";
import { Entity } from "@common/types/entity";
import { WorkspaceMember } from "@common/types/workspace";
import { getPossessive } from "@common/helpers/string";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { submitHelper } from "@helpers/form";
import { useUpdateWorkspaceAssignmentMutation } from "@api/workspaceMember.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import styles from "./MemberManagement.module.scss";
import { ConfigurePermissionsFields } from "./ConfigurePermissionsFields";
import { MemberPermissionsSchema } from "./types";

interface Props {
    actingUserRole: UserWorkspaceRole;
    member: WorkspaceMember;
    workspaceId: string;
    entities: Array<
        Pick<
            Entity,
            | "id"
            | "name"
            | "isBusiness"
            | "workspaceId"
            | "user"
            | "iconColor"
            | "logo"
        >
    >;
    header?: React.ReactNode;
    closeModal: () => void;
}

export const ConfigurePermissionsModal: React.FC<Props> = ({
    actingUserRole,
    member,
    workspaceId,
    entities,
    header,
    closeModal,
}) => {
    const { toast } = useToaster();
    const [loading, setLoading] = useState(false);

    const updateWorkspaceAssignment = useUpdateWorkspaceAssignmentMutation(
        member,
        workspaceId,
    );

    const form: FormikConfig<MemberPermissionsSchema> = {
        initialValues: {
            workspaceRole: member.role,
            entityAssignments: entities.reduce(
                (acc, entity) => {
                    acc[entity.id] = member.entityAssignments.some(
                        (memberAssignment) =>
                            memberAssignment.entity.id === entity.id,
                    );

                    return acc;
                },
                {} as Record<number, boolean>,
            ),
            assignToNewEntities: member.assignToNewEntities,
        },
        validate({ entityAssignments }) {
            if (!Object.values(entityAssignments).some((ea) => ea)) {
                return {
                    entityAssignments: "At least one entity must be selected",
                };
            }
        },
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({
                workspaceRole,
                entityAssignments,
                assignToNewEntities,
            }) => {
                await updateWorkspaceAssignment.mutateAsync({
                    role: workspaceRole,
                    entityAssignments: Object.keys(entityAssignments)
                        .map(Number)
                        .filter((entityId) => entityAssignments[entityId]),
                    assignToNewEntities,
                });

                closeModal();
                toast(`User permissions updated`);
            },
        }),
    };

    const modalHeader =
        header ??
        `Configure ${
            member.user.preferredName
                ? getPossessive(member.user.preferredName)
                : null
        } permissions`;

    return (
        <>
            <StandardModalHeader>{modalHeader}</StandardModalHeader>

            <section className={styles.layout}>
                <Formik {...form}>
                    {({ isValid }) => (
                        <Form>
                            <main className={styles.teamForm}>
                                <ConfigurePermissionsFields
                                    actingUserRole={actingUserRole}
                                    entities={entities}
                                />

                                <footer className={styles.footer}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="xl"
                                        onClick={closeModal}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                    <ButtonWithLoader
                                        loading={loading}
                                        size="xl"
                                        type="submit"
                                        variant="primary"
                                        disabled={!isValid}
                                    >
                                        Update permissions
                                    </ButtonWithLoader>
                                </footer>
                            </main>
                        </Form>
                    )}
                </Formik>
            </section>
        </>
    );
};
